import { Controller } from "@hotwired/stimulus";

// Reusable combobox/autocomplete: JSON search, optional create-on-the-fly, form binding.
export default class extends Controller {
  static values = {
    searchUrl: String,
    createUrl: { type: String, default: "" },
    createParam: { type: String, default: "name" },
    maxResults: { type: Number, default: 8 },
    debounceMs: { type: Number, default: 200 },
    minQueryLength: { type: Number, default: 1 },
    allowCreate: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    labelKey: { type: String, default: "label" },
    idKey: { type: String, default: "id" },
    emptyText: { type: String, default: "No matches." },
    addTextTemplate: { type: String, default: "Add '%{value}'" },
    submitOnSelect: { type: Boolean, default: false },
  };

  static targets = ["input", "hidden", "dropdown", "list", "status"];

  connect() {
    this.debounceTimer = null;
    this.cache = new Map();
    this.lastRequestId = 0;
    this.highlightedIndex = -1;
    this.results = [];
    this.lightModeOnly = this.element.closest(".sa-light-mode") != null;
    // Fallback when hidden target not in DOM (Stimulus 3 targets are read-only, can't assign)
    if (!this.hasHiddenTarget) {
      this._hiddenField = this.element.querySelector('input[type="hidden"]') || null;
    }
  }

  get hiddenField() {
    return this.hasHiddenTarget ? this.hiddenTarget : this._hiddenField;
  }

  get hasHiddenField() {
    return this.hiddenField != null;
  }

  input() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.search(), this.debounceMsValue);
  }

  focus() {
    const q = (this.inputTarget?.value || "").trim();
    const hasSelection = this.hasHiddenField && this.hiddenField.value;

    // When opening with a current selection, show top results so user can change (not only the selected item)
    if (hasSelection && this.minQueryLengthValue <= 0) {
      this.fetchResults("");
      return;
    }
    if (q.length === 0 && this.minQueryLengthValue <= 0) {
      this.fetchResults("");
      return;
    }
    if (q.length >= this.minQueryLengthValue) {
      this.search();
    } else {
      this.closeDropdown();
    }
  }

  blur() {
    // Delay to allow click on dropdown item to fire first
    setTimeout(() => this.closeDropdown(), 150);
  }

  keydown(e) {
    if (!this.hasDropdownTarget || !this.dropdownTarget.classList.contains("block")) {
      if (e.key === "Escape") {
        this.closeDropdown();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.highlightNext();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      this.highlightPrev();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      this.selectHighlighted();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this.closeDropdown();
      return;
    }
  }

  async search() {
    const q = (this.inputTarget?.value || "").trim();
    const minLen = this.minQueryLengthValue;

    if (q.length < minLen) {
      if (q.length === 0) {
        this.inputChangedSinceSelect();
        if (minLen === 0 && this.searchUrlValue) {
          await this.fetchResults("");
        } else {
          this.closeDropdown();
        }
      } else {
        this.closeDropdown();
      }
      return;
    }

    this.inputChangedSinceSelect();
    await this.fetchResults(q);
  }

  inputChangedSinceSelect() {
    const currentLabel = (this.inputTarget?.value || "").trim();
    const selectedLabel = this.getSelectedLabelFromResults();
    if (this.hasHiddenField && selectedLabel && currentLabel !== selectedLabel) {
      this.hiddenField.value = "";
      this.hiddenField.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  getSelectedLabelFromResults() {
    const id = this.hiddenField?.value;
    if (!id) return null;
    const r = this.results.find((x) => String(x[this.idKeyValue]) === String(id));
    return r ? (r[this.labelKeyValue] || r.name) : null;
  }

  async fetchResults(q) {
    if (!this.searchUrlValue) return;

    const cacheKey = q;
    if (this.cache.has(cacheKey)) {
      this.renderResults(this.cache.get(cacheKey), q);
      return;
    }

    const requestId = ++this.lastRequestId;
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", q);
    url.searchParams.set("limit", String(this.maxResultsValue));

    try {
      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
      });
      if (requestId !== this.lastRequestId) return;

      const data = await res.json();
      const list = Array.isArray(data) ? data : data.results || data.items || [];
      this.cache.set(cacheKey, list);
      this.renderResults(list, q);
    } catch (err) {
      if (requestId !== this.lastRequestId) return;
      this.showStatus("Search failed.", true);
    }
  }

  renderResults(list, q) {
    this.results = list;
    this.highlightedIndex = -1;
    const listEl = this.hasListTarget ? this.listTarget : null;
    if (!listEl) return;

    listEl.innerHTML = "";
    const hasExactMatch = list.some(
      (x) =>
        (x[this.labelKeyValue] || x.name || "")
          .toLowerCase()
          .trim() === q.toLowerCase()
    );
    const showAdd = this.allowCreateValue && this.createUrlValue && q.length > 0 && !hasExactMatch;

    if (list.length === 0 && !showAdd) {
      const li = document.createElement("li");
      li.className = this.lightModeOnly
        ? "px-3 py-2 text-sm text-gray-600"
        : "px-3 py-2 text-sm text-gray-500 dark:text-gray-400";
      li.textContent = this.emptyTextValue;
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", "false");
      listEl.appendChild(li);
    } else {
      list.forEach((item, idx) => {
        const li = this.createResultItem(item, idx);
        listEl.appendChild(li);
      });
      if (showAdd) {
        const addLi = document.createElement("li");
        const addText = this.addTextTemplateValue.replace("%{value}", q);
        addLi.className = this.lightModeOnly
          ? "px-3 py-2 text-sm text-primary-600 hover:bg-gray-50 cursor-pointer border-t border-gray-200"
          : "px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-t border-gray-100 dark:border-gray-700";
        addLi.innerHTML = `<span class="font-medium">${addText}</span>`;
        addLi.setAttribute("role", "option");
        addLi.setAttribute("data-action", "click->forms--combobox#createAndSelect");
        addLi.dataset.value = q;
        listEl.appendChild(addLi);
      }
    }

    this.showDropdown();
  }

  createResultItem(item, idx) {
    const li = document.createElement("li");
    const id = item[this.idKeyValue] ?? item.id;
    const label = item[this.labelKeyValue] ?? item.name ?? String(id);
    const sublabel = item.sublabel ?? item.sub_label ?? "";
    li.className = this.lightModeOnly
      ? "px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer"
      : "px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer";
    if (sublabel) {
      // Two-line option: primary label + a classifying subtitle (e.g. "Staged" / "Existing").
      const main = document.createElement("div");
      main.className = "font-medium";
      main.textContent = label;
      const sub = document.createElement("div");
      sub.className = this.lightModeOnly
        ? "text-xs text-gray-500"
        : "text-xs text-gray-500 dark:text-gray-400";
      sub.textContent = sublabel;
      li.appendChild(main);
      li.appendChild(sub);
    } else {
      li.textContent = label;
    }
    li.setAttribute("role", "option");
    li.setAttribute("data-id", id);
    li.setAttribute("data-label", label);
    li.setAttribute("data-index", idx);
    li.setAttribute("data-action", "mousedown->forms--combobox#select");
    return li;
  }

  select(e) {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    const id = el.getAttribute("data-id");
    const label = el.getAttribute("data-label");
    if (id != null && label != null) {
      this.setSelection(id, label);
    }
    this.closeDropdown();
  }

  async createAndSelect(e) {
    const value = e.currentTarget.dataset?.value || this.inputTarget?.value?.trim();
    if (!value || !this.createUrlValue) return;

    this.showStatus("Creating…");
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content || "";
    const formData = new FormData();
    formData.append(this.createParamValue, value);
    formData.append("authenticity_token", csrfToken);

    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data[this.idKeyValue] != null) {
        const id = data[this.idKeyValue];
        const label = data[this.labelKeyValue] ?? data.name ?? value;
        this.setSelection(id, label);
        this.cache.clear();
        this.hideStatus();
        this.closeDropdown();
      } else {
        this.showStatus(data.error || data.message || "Failed to create.", true);
      }
    } catch (err) {
      this.showStatus("Failed to create.", true);
    }
  }

  setSelection(id, label) {
    const form = this.element.closest("form");
    if (form && this.hasHiddenField && id) {
      form.dispatchEvent(new CustomEvent("member-selected", {
        bubbles: true,
        detail: { source: "manual", label: label || "", id }
      }));
    }
    // Set visible input BEFORE hidden+change so change listeners (e.g. rank-skills#skillSelected)
    // see the correct label when they read the input value. Previously change fired first,
    // causing listeners to get empty label when user selected without typing.
    if (this.inputTarget) {
      this.inputTarget.value = label;
      this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = id;
      this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (this.submitOnSelectValue) {
      const form = this.element.closest("form");
      if (form) {
        requestAnimationFrame(() => form.requestSubmit());
      }
    }
  }

  highlightNext() {
    const items = this.listTarget?.querySelectorAll('[role="option"][data-id]') || [];
    if (items.length === 0) return;
    this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1);
    this.updateHighlight(items);
  }

  highlightPrev() {
    const items = this.listTarget?.querySelectorAll('[role="option"][data-id]') || [];
    if (items.length === 0) return;
    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
    this.updateHighlight(items);
  }

  updateHighlight(items) {
    const highlightClasses = this.lightModeOnly ? ["bg-gray-100"] : ["bg-gray-100", "dark:bg-gray-700"];
    items.forEach((item, i) => {
      if (i === this.highlightedIndex) {
        item.classList.add(...highlightClasses);
        item.scrollIntoView({ block: "nearest" });
      } else {
        item.classList.remove(...highlightClasses);
      }
    });
  }

  selectHighlighted() {
    const items = this.listTarget?.querySelectorAll('[role="option"][data-id]') || [];
    const item = items[this.highlightedIndex];
    if (item) {
      const id = item.getAttribute("data-id");
      const label = item.getAttribute("data-label");
      if (id != null && label != null) this.setSelection(id, label);
    }
    this.closeDropdown();
  }

  showDropdown() {
    if (!this.hasDropdownTarget) return;
    this.dropdownTarget.classList.remove("hidden");
    this.dropdownTarget.classList.add("block");
  }

  closeDropdown() {
    if (!this.hasDropdownTarget) return;
    this.dropdownTarget.classList.add("hidden");
    this.dropdownTarget.classList.remove("block");
    this.highlightedIndex = -1;
  }

  showStatus(msg, isError = false) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = msg;
      this.statusTarget.classList.remove("hidden");
      if (isError) {
        this.statusTarget.classList.add("status-error");
      } else {
        this.statusTarget.classList.remove("status-error");
      }
    }
  }

  hideStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("hidden");
      this.statusTarget.classList.remove("status-error");
    }
  }
}
